import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Sparkles,
  Wand2,
  ImageIcon,
  Wrench,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  MessageSquare,
  Loader2,
  UserCircle2,
  Bot,
  ListChecks,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { postsAPI, getErrorMessage } from '../../services/api';

// ────────────────────────────────────────────────────────────────────
// Types — mirrors the /posts/{id}/creation-history backend response.
// ────────────────────────────────────────────────────────────────────

interface CreationHistoryMessage {
  role: 'user' | 'assistant' | null;
  content: string;
  timestamp: string | null;
  event_type: string | null;
  step: string | null;
  values: Record<string, unknown> | null;
  action: string | null;
  tool_calls: Array<Record<string, unknown>> | null;
  platforms: string[] | null;
  hashtags: string[] | null;
  content_preview: Record<string, string> | null;
  all_passed: boolean | null;
  issue_count: number | null;
  media_id: string | null;
  new_media_id: string | null;
  platform: string | null;
  strategy: string | null;
  post_data: Record<string, unknown> | null;
  inline_blocks: Array<Record<string, unknown>> | null;
  resume_data: Record<string, unknown> | null;
  resumed: boolean | null;
  wizard_session: Record<string, unknown> | null;
}

interface CreationHistoryResponse {
  post_id: string;
  conversation_id: string | null;
  thread_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  title: string | null;
  messages: CreationHistoryMessage[];
  summary: {
    total_events: number;
    step_count: number;
    has_ai_generate: boolean;
    has_media_validate: boolean;
    has_media_fix: boolean;
    has_ai_review: boolean;
    errors: number;
  };
}

interface PostCreationHistoryProps {
  postId: string;
}

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

const eventMeta: Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; color: string; bg: string }
> = {
  wizard_start: { icon: Sparkles, color: '#6366f1', bg: '#eef2ff' },
  wizard_step: { icon: ListChecks, color: '#0ea5e9', bg: '#e0f2fe' },
  wizard_step_result: { icon: Bot, color: '#64748b', bg: '#f1f5f9' },
  ai_generate: { icon: Wand2, color: '#a855f7', bg: '#faf5ff' },
  media_validate: { icon: ShieldCheck, color: '#14b8a6', bg: '#ccfbf1' },
  media_fix: { icon: Wrench, color: '#f59e0b', bg: '#fef3c7' },
  ai_review: { icon: Bot, color: '#8b5cf6', bg: '#f5f3ff' },
  review_action: { icon: CheckCircle2, color: '#22c55e', bg: '#dcfce7' },
  post_created: { icon: CheckCircle2, color: '#16a34a', bg: '#dcfce7' },
  error: { icon: AlertTriangle, color: '#dc2626', bg: '#fee2e2' },
};

const defaultEventMeta = { icon: MessageSquare, color: '#64748b', bg: '#f1f5f9' };

function formatTimestamp(iso: string | null): string {
  if (!iso) {
    return '';
  }
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function renderEventDetails(
  msg: CreationHistoryMessage,
  t: TFunction,
): React.ReactNode {
  // Step answers — render key/value pills.
  if (msg.event_type === 'wizard_step' && msg.values) {
    const entries = Object.entries(msg.values).filter(([, v]) => v !== null && v !== '');
    if (entries.length === 0) {
      return null;
    }
    return (
      <div className="mt-1.5 flex flex-wrap gap-1">
        {entries.map(([key, value]) => (
          <span
            key={key}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200"
          >
            <span className="font-semibold text-slate-500">{key}:</span>
            <span className="truncate max-w-[200px]">
              {Array.isArray(value)
                ? value.join(', ')
                : typeof value === 'object'
                  ? JSON.stringify(value).slice(0, 60)
                  : String(value).slice(0, 80)}
            </span>
          </span>
        ))}
      </div>
    );
  }

  // AI generate — show platforms and hashtags.
  if (msg.event_type === 'ai_generate') {
    return (
      <div className="mt-1.5 space-y-1">
        {msg.platforms && msg.platforms.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {msg.platforms.map((p) => (
              <span
                key={p}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200"
              >
                {p}
              </span>
            ))}
          </div>
        )}
        {msg.hashtags && msg.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {msg.hashtags.slice(0, 10).map((tag) => (
              <span key={tag} className="text-[10px] text-slate-500">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Media validate — show pass/fail + count.
  if (msg.event_type === 'media_validate') {
    const passed = msg.all_passed === true;
    const count = msg.issue_count ?? 0;
    return (
      <div className="mt-1.5">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${
            passed
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-amber-50 text-amber-700 border-amber-200'
          }`}
        >
          {passed ? (
            <>
              <CheckCircle2 className="w-3 h-3" />
              {t('history_media_all_passed', 'All media passed')}
            </>
          ) : (
            <>
              <AlertTriangle className="w-3 h-3" />
              {t('history_media_issues_found', '{{count}} issue(s) found', { count })}
            </>
          )}
        </span>
      </div>
    );
  }

  // Media fix — show platform + strategy.
  if (msg.event_type === 'media_fix') {
    return (
      <div className="mt-1.5 flex flex-wrap gap-1">
        {msg.platform && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
            <ImageIcon className="w-3 h-3" />
            {msg.platform}
          </span>
        )}
        {msg.strategy && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200">
            <Wrench className="w-3 h-3" />
            {msg.strategy}
          </span>
        )}
      </div>
    );
  }

  // Post created — show final status + platforms.
  if (msg.event_type === 'post_created' && msg.post_data) {
    const status = (msg.post_data.status as string) || 'draft';
    const platforms = (msg.post_data.platforms as string[] | undefined) || [];
    return (
      <div className="mt-1.5 flex flex-wrap gap-1">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
          <CheckCircle2 className="w-3 h-3" />
          {status}
        </span>
        {platforms.map((p) => (
          <span
            key={p}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200"
          >
            {p}
          </span>
        ))}
      </div>
    );
  }

  return null;
}

// ────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────

export const PostCreationHistory: React.FC<PostCreationHistoryProps> = ({ postId }) => {
  const { t } = useTranslation();

  const { data, isLoading, isError, error } = useQuery<CreationHistoryResponse>({
    queryKey: ['post', postId, 'creation-history'],
    queryFn: () =>
      postsAPI
        .creationHistory(postId)
        .then((r) => r.data as CreationHistoryResponse),
    enabled: !!postId,
    staleTime: 60_000,
    retry: 1,
  });

  // Memoised filtered message list — drop empty/no-op messages.
  const messages = useMemo<CreationHistoryMessage[]>(() => {
    if (!data?.messages) {
      return [];
    }
    return data.messages.filter((m) => m.content || m.event_type);
  }, [data]);

  React.useEffect(() => {
    if (isError && error) {
      toast.error(
        getErrorMessage(error, t('history_load_failed', 'Could not load creation history')),
      );
    }
  }, [isError, error, t]);

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 text-xs text-slate-500 py-2"
        role="status"
        aria-busy="true"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        {t('history_loading', 'Loading creation history…')}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-xs text-rose-600 py-2">
        {t('history_load_failed', 'Could not load creation history')}
      </div>
    );
  }

  if (!data || messages.length === 0) {
    return (
      <div className="text-xs text-slate-500 py-2">
        {t('history_empty', 'No creation history recorded for this post.')}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary strip */}
      <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-medium">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
          <ListChecks className="w-3 h-3" />
          {t('history_events', '{{count}} events', { count: data.summary.total_events })}
        </span>
        {data.summary.step_count > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200">
            {t('history_steps', '{{count}} steps', { count: data.summary.step_count })}
          </span>
        )}
        {data.summary.has_ai_generate && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 border border-purple-200">
            <Wand2 className="w-3 h-3" />
            {t('history_ai_generated', 'AI generated')}
          </span>
        )}
        {data.summary.has_media_validate && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 border border-teal-200">
            <ShieldCheck className="w-3 h-3" />
            {t('history_media_checked', 'Media checked')}
          </span>
        )}
        {data.summary.has_media_fix && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
            <Wrench className="w-3 h-3" />
            {t('history_media_fixed', 'Media auto-fixed')}
          </span>
        )}
        {data.summary.errors > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200">
            <AlertTriangle className="w-3 h-3" />
            {t('history_errors', '{{count}} errors', { count: data.summary.errors })}
          </span>
        )}
      </div>

      {/* Timeline */}
      <ol className="relative space-y-2.5 border-l border-slate-200 pl-4" aria-label={t('history_timeline', 'Creation timeline')}>
        {messages.map((msg, idx) => {
          const meta = (msg.event_type && eventMeta[msg.event_type]) || defaultEventMeta;
          const Icon = meta.icon;
          const isUser = msg.role === 'user';
          const RoleIcon = isUser ? UserCircle2 : Bot;
          const details = renderEventDetails(msg, t);

          return (
            <li key={idx} className="relative">
              {/* Timeline dot */}
              <span
                className="absolute -left-[22px] top-1 flex h-4 w-4 items-center justify-center rounded-full ring-2 ring-white"
                style={{ backgroundColor: meta.bg }}
                aria-hidden="true"
              >
                <Icon className="w-2.5 h-2.5" style={{ color: meta.color }} />
              </span>

              <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                <div className="flex items-start gap-1.5">
                  <RoleIcon
                    className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${
                      isUser ? 'text-indigo-500' : 'text-slate-400'
                    }`}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        {msg.event_type || (isUser ? t('history_user', 'User') : t('history_assistant', 'Assistant'))}
                      </span>
                      <time className="text-[10px] text-slate-400">
                        {formatTimestamp(msg.timestamp)}
                      </time>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-700 break-words">
                      {msg.content || t('history_no_content', '(no content)')}
                    </p>
                    {details}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default PostCreationHistory;
